import React, { useState } from 'react';
import { Trans } from '@lingui/react/macro';
import { Modal, ModalHeader, ModalBody, ModalFooter, Banner, LoadingIndicator, Toast } from '@ygtechnologies/bygr-frontend-component-library';
import { Button } from '@/components/Buttons';
import { deleteResidentialUnit, getResidentialUnitBuyerInfo } from '@/services/residential-units';
import { BuyerInfo, ResidentialUnit } from '@/types';
import { triggerHandler } from '@ygtechnologies/rubus-frontend-utilities';
import { useProjectRelativeRedirect } from '@/utils/hooks';

interface ResidentialUnitDeleteDialogProps {
    residentialUnit: ResidentialUnit;
    show: boolean;
    onClose: () => void;
    onDeleted?: () => void;
    redirectAfterDelete?: boolean;
}

export const ResidentialUnitDeleteDialog: React.FC<ResidentialUnitDeleteDialogProps> = ({
    residentialUnit,
    show,
    onClose,
    onDeleted,
    redirectAfterDelete,
}) => {
    const [isDeleting, setIsDeleting] = useState(false);
    const linkFunction = useProjectRelativeRedirect();
    const [buyerInfo, setBuyerInfo] = useState<BuyerInfo | null>();

    const onOpen = async () => {
        setBuyerInfo(await getResidentialUnitBuyerInfo(residentialUnit.id));
    };

    const handleDelete = async () => {
        setIsDeleting(true);
        try {
            await deleteResidentialUnit(residentialUnit.id);
            Toast.success(<Trans>Unit deleted successfully</Trans>);
            triggerHandler(onDeleted);
            onClose();
            if (redirectAfterDelete) {
                linkFunction('/residential-units');
            }
        } catch (error) {
            Toast.error(<Trans>Failed to delete unit</Trans>);
        } finally {
            setIsDeleting(false);
        }
    };
    return (
        <Modal onOpen={onOpen} isOpen={show} toggle={onClose}>
            <ModalHeader>
                <Trans>Delete Unit</Trans>
            </ModalHeader>
            {buyerInfo == null ? (
                <LoadingIndicator />
            ) : (
                <>
                    <ModalBody>
                        <p>
                            <Trans>
                                Are you sure you want to delete the unit <strong>{residentialUnit.name}</strong>?
                            </Trans>
                        </p>

                        {buyerInfo.users.length > 0 ? (
                            <Banner type="danger">
                                <Trans>
                                    You cannot delete this unit while there are buyers connected to it. Please remove all
                                    buyers before deleting the unit.
                                </Trans>
                            </Banner>
                        ) : (
                            <Banner type="warning">
                                <ul>
                                    <Trans id="ResidentialUnitDeleteDialog.warning">
                                        <li>This action will permanently remove the unit and all associated data.</li>
                                        <li>This action cannot be undone.</li>
                                    </Trans>
                                </ul>
                            </Banner>
                        )}
                    </ModalBody>

                    <ModalFooter>
                        <Button color="tertiary" onClick={onClose}>
                            <Trans>Cancel</Trans>
                        </Button>
                        <Button
                            disabled={buyerInfo.users.length > 0}
                            color="danger"
                            loading={isDeleting}
                            onClick={handleDelete}>
                            <Trans>Delete Unit</Trans>
                        </Button>
                    </ModalFooter>
                </>
            )}
        </Modal>
    );
};
